/// <amd-module name="Wit/WorkItemGitLinks/Components/Commit/CommitPickerDialog" />
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { usePageContext } from "VSS/Platform/Layout";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { getGitClient, IGitRestClient } from "VC/Common/RestClient/Git";
import { GitRepository, GitCommitRef, GitQueryCommitsCriteria, GitVersionType, GitVersionOptions } from "VC/Common/Contracts/Git";
import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { CommitPickerTable } from "./CommitPickerTable";
import { CommitFilterForm, defaultCommitFilters, ICommitFilters } from "./CommitFilterForm";
import { useDebouncedSubscription, useObservable } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { formatBranchName, formatTagName } from "Wit/WorkItemGitLinks/GitLinkUtils";

interface ICommitPickerDialogProps {
    repository: GitRepository;
    onCommitChosen: (newCommitSHA: string) => void;
    onDismiss: () => void;
}

export function CommitPickerDialog({ repository, onCommitChosen, onDismiss }: ICommitPickerDialogProps) {
    const pageContext = usePageContext();
    const gitClient = useRef<IGitRestClient>(getGitClient(pageContext));
    const [commits, setCommits] = useState<GitCommitRef[]>([]);
    const [selectedCommit, setSelectedCommit] = useState<string | undefined>(undefined);
    const [commitFiltersObservable, setCommitFilters] = useObservable<ICommitFilters>(defaultCommitFilters);
    const [errorObservable, setError] = useObservable<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    // On initialization, initialize the commit filters to fire off an initial search
    useEffect(() => {
        setTimeout(() => setCommitFilters(defaultCommitFilters), 1);
    }, []);

    // When the filters change, find matching commits
    useDebouncedSubscription(commitFiltersObservable, 400, commitFilters => {
        setLoading(true);
        fetchMatchingCommits(gitClient.current, repository, commitFilters)
            .then(commits => {
                setError(undefined);
                setCommits(commits);
                setLoading(false);
            })
            .catch((e: unknown) => {
                if (e instanceof Error) {
                    setError(e.message);
                } else {
                    setError("" + e);
                }
                setCommits([]);
                setLoading(false);
            });
    });

    return (
        <Dialog
            titleProps={{ text: Resources.CommitPickerDialogTitle }}
            className="commit-picker-dialog"
            onDismiss={onDismiss}
            contentSize={ContentSize.ExtraLarge}
            footerButtonProps={[
                { text: Resources.CommitPickerDialogCancelButtonLabel, onClick: onDismiss },
                {
                    text: Resources.CommitPickerDialogConfirmButtonLabel,
                    onClick: () => onCommitChosen(selectedCommit!),
                    disabled: !selectedCommit,
                    primary: true
                }
            ]}
        >
            <Observer commitFilters={commitFiltersObservable}>
                {({ commitFilters }: { commitFilters: ICommitFilters }) => (
                    <CommitFilterForm
                        repository={repository}
                        commitFilters={commitFilters}
                        onCommitFiltersChanged={setCommitFilters}
                    />
                )}
            </Observer>

            <Observer error={errorObservable}>
                {({ error }: { error: string | undefined }) => (error ? <div className="error-text">{error}</div> : null)}
            </Observer>

            <div className="commit-picker-table-container">
                <CommitPickerTable loading={loading} commits={commits} onCommitSelected={setSelectedCommit} />
            </div>
        </Dialog>
    );
}

async function fetchMatchingCommits(gitClient: IGitRestClient, repository: GitRepository, commitFilters: ICommitFilters): Promise<GitCommitRef[]> {
    return gitClient.getCommits(
        repository.id,
        {
            $top: 50,
            historyMode: commitFilters.gitLogHistoryMode,
            author: commitFilters.author?.mail ?? commitFilters.author?.displayName,
            itemPath: commitFilters.path,
            itemVersion: commitFilters.ref
                ? {
                      version: commitFilters.ref.name.startsWith("refs/heads")
                          ? formatBranchName(commitFilters.ref.name)
                          : formatTagName(commitFilters.ref.name),
                      versionType: commitFilters.ref.name.startsWith("refs/heads") ? GitVersionType.Branch : GitVersionType.Tag,
                      versionOptions: GitVersionOptions.None
                  }
                : undefined,
            fromDate: commitFilters.fromDate?.toISOString(),
            toDate: commitFilters.toDate?.toISOString()
        } as GitQueryCommitsCriteria,
        repository.project.id
    );
}
